<script lang="ts">
import type { Snippet } from 'svelte';
import {
  ContextMenuItem,
  ContextMenuSeparator,
  FloatingMenu,
  Icon,
  Tooltip,
} from '@viamrobotics/prime-core';
import type { BuildInfo } from '@viamrobotics/web-client/environment/v1/environment';
import { EnvironmentType } from '@viamrobotics/web-client/environment/v1/environment';
import { useTooltipWarmup } from '$lib/tooltip-warmup.svelte';

import { createGetEnvironmentQuery } from '$lib/api/environment';
import logo from '$lib/assets/viam-logo-white.svg';
import {
  enableDebugLogging,
  disableDebugLogging,
  isDebugLoggingEnabled,
} from '$lib/api/debug-logging';
import { useUserContext } from '$lib/api/user';
import type { AuthState } from '$lib/auth';
import { AuthStatus } from '$lib/auth';
import { isViamUser } from '$lib/auth/user-info';
import { Link } from '$lib/components/link';

import NavHelpButtons from './nav-help-buttons.svelte';
import type { Page } from './pages';

export interface Props {
  pageLinks: Page[];
  authState: AuthState;
  enableInkeep: boolean;
  children?: Snippet;
}

const { pageLinks, authState, enableInkeep, children }: Props = $props();

const warmup = useTooltipWarmup(800);

let contextMenuOpen = $state(false);
const setContextMenuOpen = (isOpen: boolean) => (contextMenuOpen = isOpen);

let debugLoggingEnabled = $state(isDebugLoggingEnabled());

const { user } = useUserContext();

const showContextMenu = $derived(
  authState.status === AuthStatus.LoggedIn && isViamUser(authState.user)
);

// The logo only links to `/fleet` once we know the visitor is signed in.
// `/fleet` is auth-required and hover preload runs its load function, so
// linking it on a public page bounces anonymous visitors to login. Until auth
// resolves we render the logo unlinked rather than guess a public destination.
const isLoggedIn = $derived(authState.status === AuthStatus.LoggedIn);

const padTwo = (value: number) => String(value).padStart(2, '0');

// Renders an RFC3339 commit timestamp as a compact UTC string (e.g. 20260718T1930Z),
// falling back to the raw value if it can't be parsed.
const compactCommitTime = (commitTime: string): string => {
  if (!commitTime) {
    return '?';
  }
  const date = new Date(commitTime);
  if (Number.isNaN(date.getTime())) {
    return commitTime;
  }
  return (
    `${date.getUTCFullYear()}${padTwo(date.getUTCMonth() + 1)}${padTwo(date.getUTCDate())}` +
    `T${padTwo(date.getUTCHours())}${padTwo(date.getUTCMinutes())}Z`
  );
};

// Composes the build identifier, e.g. "v0.1.569-20260718T1930Z-1099f808 (go1.25.12)".
// STAGING_TEMP builds carry a squashed CI commit that doesn't exist on GitHub, so their
// commit portion renders as "squashed" rather than the meaningless hash.
const formatBuildVersion = (info: BuildInfo, squashed: boolean): string => {
  const apiVersion = info.apiVersion || '?';
  const commit = squashed ? 'squashed' : info.gitCommit.slice(0, 8);
  return `${apiVersion}-${compactCommitTime(info.commitTime)}-${commit} (${info.goVersion})`;
};

const envQuery = createGetEnvironmentQuery();
const buildInfo = $derived($envQuery.data?.buildInfo);
const isSquashedBuild = $derived(
  $envQuery.data?.environmentType === EnvironmentType.STAGING_TEMP
);
const buildLabel = $derived(
  buildInfo ? formatBuildVersion(buildInfo, isSquashedBuild) : undefined
);
// No commit link for squashed builds — the hash doesn't resolve on GitHub.
const commitUrl = $derived(
  buildInfo?.gitCommit && !isSquashedBuild
    ? `https://github.com/viamrobotics/app/commit/${buildInfo.gitCommit}`
    : undefined
);

const handleInternalHomeClick = () => {
  setContextMenuOpen(false);
  window.open('/internal', '_blank');
};

const handleFeatureFlagsClick = () => {
  setContextMenuOpen(false);
  window.open('/internal/feature-flags-admin', '_blank');
};

const handleOrgAdminClick = () => {
  setContextMenuOpen(false);
  window.open('/internal/org-admin', '_blank');
};

const handleRegistryAdminClick = () => {
  setContextMenuOpen(false);
  window.open('/internal/registry-admin', '_blank');
};

const handleReleaseAdminClick = () => {
  setContextMenuOpen(false);
  window.open('/internal/release-admin', '_blank');
};

const handleOpsBillingDashboardClick = () => {
  setContextMenuOpen(false);
  window.open('/internal/ops-billing-dashboard', '_blank');
};

const handleAiAssistantStatsClick = () => {
  setContextMenuOpen(false);
  window.open('/internal/ai-assistant-stats', '_blank');
};

const handleOpenInNewTab = () => {
  setContextMenuOpen(false);
  window.open('/fleet', '_blank');
};

const handleDebugLoggingToggle = () => {
  if (debugLoggingEnabled) {
    disableDebugLogging();
    debugLoggingEnabled = false;
  } else {
    enableDebugLogging();
    debugLoggingEnabled = true;
  }
  setContextMenuOpen(false);
};
</script>

<nav
  class="sm:min-h-[52px] z-30 flex flex-none flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-4 px-3 sm:px-6 py-1.5 pb-3 sm:pb-1.5 text-sm border-b"
>
  {#snippet logoImage()}
    <img
      src={logo}
      alt="VIAM"
      class="shrink-0 h-[13px] brightness-0"
    />
  {/snippet}

  {#if showContextMenu}
    <FloatingMenu
      isOpen={contextMenuOpen}
      onChange={setContextMenuOpen}
      placement="bottom-start"
      offset={4}
    >
      <svelte:fragment slot="control">
        <Link
          href="/fleet"
          class="flex shrink-0"
          onclick={(event) => {
            // Prevent Viam Admin FloatingMenu from opening on left-click
            event.stopPropagation();
          }}
          oncontextmenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setContextMenuOpen(true);
          }}
        >
          {@render logoImage()}
        </Link>
      </svelte:fragment>
      <svelte:fragment slot="items">
        <ContextMenuItem
          icon="open-in-new"
          on:click={() => {
            handleOpenInNewTab();
          }}
        >
          Open link as new tab
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          icon="view-dashboard-outline"
          on:click={() => {
            handleInternalHomeClick();
          }}
        >
          All internal pages
        </ContextMenuItem>
        <ContextMenuItem
          icon="toggle-switch"
          on:click={() => {
            handleFeatureFlagsClick();
          }}
        >
          Feature Flags admin dashboard
        </ContextMenuItem>
        {#if $user?.isOrgAdmin}
          <ContextMenuItem
            icon="account-plus-outline"
            on:click={() => {
              handleOrgAdminClick();
            }}
          >
            Org admin dashboard
          </ContextMenuItem>
        {/if}
        <ContextMenuItem
          icon="viam-registry-module"
          on:click={() => {
            handleRegistryAdminClick();
          }}
        >
          Registry admin dashboard
        </ContextMenuItem>
        <ContextMenuItem
          icon="package-variant-closed"
          on:click={() => {
            handleReleaseAdminClick();
          }}
        >
          Release admin dashboard
        </ContextMenuItem>
        <ContextMenuItem
          icon="credit-card-outline"
          on:click={() => {
            handleOpsBillingDashboardClick();
          }}
        >
          Ops Billing dashboard
        </ContextMenuItem>
        <ContextMenuItem
          icon="sparkles"
          on:click={() => {
            handleAiAssistantStatsClick();
          }}
        >
          AI Assistant stats dashboard
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          icon={debugLoggingEnabled
            ? 'checkbox-marked'
            : 'checkbox-blank-outline'}
          on:click={() => {
            handleDebugLoggingToggle();
          }}
        >
          Debug logging
        </ContextMenuItem>
        {#snippet buildInfoRow()}
          <div class="text-gray-400">
            <Icon name="information-outline" />
          </div>
          <p class="text-subtle-2 text-sm">{buildLabel}</p>
        {/snippet}
        {#if buildLabel}
          <ContextMenuSeparator />
          {#if commitUrl}
            <a
              href={commitUrl}
              target="_blank"
              rel="noreferrer"
              class="flex w-full items-center gap-1.5 px-3 py-1.5 hover:bg-light"
            >
              {@render buildInfoRow()}
            </a>
          {:else}
            <div class="flex w-full items-center gap-1.5 px-3 py-1.5">
              {@render buildInfoRow()}
            </div>
          {/if}
        {/if}
      </svelte:fragment>
    </FloatingMenu>
  {:else if isLoggedIn}
    <Link
      href="/fleet"
      class="flex shrink-0"
    >
      {@render logoImage()}
    </Link>
  {:else}
    {@render logoImage()}
  {/if}

  {#if pageLinks.length > 0}
    <div class="hidden sm:flex ml-5 mr-2 w-px bg-gray-4 h-4"></div>
  {/if}

  <ul
    class="flex w-full sm:w-auto sm:flex-grow items-center text-center order-3 sm:order-none basis-full sm:basis-auto"
  >
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="-ml-3 sm:ml-0 flex flex-row items-center gap-2"
      onmouseenter={warmup.onGroupMouseEnter}
      onmouseleave={warmup.onGroupMouseLeave}
    >
      {#each pageLinks as page (page.href)}
        <li class="flex">
          <Tooltip
            location="bottom"
            hoverDelayMS={warmup.delay}
            state={page.tooltip ? undefined : 'invisible'}
            let:tooltipID
          >
            <Link
              {...page}
              class="flex items-center h-8 rounded-sm px-3 text-[13px] font-roboto-mono uppercase tracking-wider hover:bg-ghost-light"
              aria-current={page.isCurrent ? 'page' : undefined}
              aria-describedby={tooltipID}
            >
              <span
                class={`py-0.5 ${
                  page.isCurrent ? 'font-bold text-default' : 'text-subtle-2'
                }`}
              >
                {page.name}
              </span>
            </Link>
            <p slot="description">{page.tooltip}</p>
          </Tooltip>
        </li>
      {/each}
    </div>

    <div class="ml-auto mr-2 sm:mr-0 flex items-center">
      <NavHelpButtons
        {authState}
        {enableInkeep}
      />
    </div>
  </ul>

  <div class="ml-auto sm:ml-0 flex flex-shrink-0 gap-2 self-stretch">
    {#if children}
      {@render children()}
    {/if}
  </div>
</nav>
